import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Database, Loader2, RefreshCw } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import {
  bindDreaminaProxyGroup, createDreaminaProxyGroup, listDreaminaProxyAssets,
  listDreaminaProxyGroups, readDreaminaProxyGroup,
} from '../../services/adminDreaminaProxyLibraryService';

const readText = (...values) => values.find((value) => typeof value === 'string' && value.trim())?.trim() || '';
const inputClass = 'w-full rounded-xl border border-[var(--color-border-primary)] bg-[var(--color-bg-tertiary)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-success)]';

export default function DreaminaProxyLibraryPanel({ refreshSequence = 0 }) {
  const toast = useToast();
  const [info, setInfo] = useState(null);
  const [groups, setGroups] = useState([]);
  const [assets, setAssets] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [groupId, setGroupId] = useState('');
  const [candidateId, setCandidateId] = useState('');
  const [confirmAdditional, setConfirmAdditional] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError('');
    readDreaminaProxyGroup(abort.signal).then((result) => {
      if (abort.signal.aborted) return;
      setInfo(result);
      setProjectName((current) => current || result?.projectName || '');
      if (result?.configured) {
        listDreaminaProxyAssets(abort.signal).then((data) => {
          if (!abort.signal.aborted) setAssets(Array.isArray(data?.Items) ? data.Items : []);
        }).catch((failure) => {
          if (!abort.signal.aborted) setError(`素材列表读取失败：${failure.message}`);
        });
      } else setAssets([]);
    }).catch((failure) => {
      if (!abort.signal.aborted) { setInfo(null); setError(failure.message || '素材组配置读取失败'); }
    }).finally(() => { if (!abort.signal.aborted) setLoading(false); });
    return () => abort.abort();
  }, [refreshSequence, revision]);

  const searchGroups = async () => {
    const project = projectName.trim();
    if (!project) { toast.warning('请填写官方 ProjectName'); return; }
    setBusy(true);
    setError('');
    try {
      const result = await listDreaminaProxyGroups(project);
      setGroups(Array.isArray(result?.Items) ? result.Items : []);
    } catch (failure) {
      setError(`素材组查询失败：${failure.message}`);
    } finally { setBusy(false); }
  };

  const createGroup = async () => {
    const name = groupName.trim();
    const project = projectName.trim();
    if (!name || !project) { toast.warning('请填写素材组名称和官方 ProjectName'); return; }
    if (info?.configured && !confirmAdditional) { toast.warning('请先确认另建候选素材组可能产生重复'); return; }
    setBusy(true);
    setError('');
    try {
      const result = await createDreaminaProxyGroup({ name, description: description.trim(), projectName: project,
        confirmAdditional: Boolean(info?.configured && confirmAdditional) });
      const id = readText(result?.groupId);
      if (!id) throw new Error('创建请求已返回，但未取得素材组 ID；请先查询官方素材组，勿立即重复创建');
      setCandidateId(id);
      setGroupId(id);
      toast.success(`候选素材组已创建：${id}；尚未绑定`);
      setGroups((current) => [{ Id: id, Name: name, ProjectName: project }, ...current]);
    } catch (failure) {
      setError(`创建结果未确认：${failure.message}。请先查询素材组，勿直接重试。`);
    } finally { setBusy(false); }
  };

  const bindGroup = async (selectedId = groupId) => {
    const id = selectedId.trim();
    const project = projectName.trim();
    if (!id || !project) { toast.warning('请填写素材组 ID 和官方 ProjectName'); return; }
    if (!window.confirm(`将 Dreamina v3 新代理绑定到 ${project} / ${id}。旧海外素材库及历史任务不变。确认继续？`)) return;
    setBusy(true);
    setError('');
    try {
      await bindDreaminaProxyGroup({ groupId: id, projectName: project });
      setCandidateId('');
      toast.success('新代理素材组已校验并绑定');
      refresh();
    } catch (failure) {
      setError(`绑定失败：${failure.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 text-[var(--color-text-primary)]">
      <div className="rounded-2xl border border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold"><Database size={18} className="text-[var(--color-success)]" />海外 Dreamina v3 新代理素材组</div>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">仅供海外 2.0 / Fast 新任务入库。旧 EngGuaVideo / Omini 素材 ID 不会被当作新库就绪；有原图可重新入库，只有旧 ID 则需重新选择素材。海外 2.5 在独立的“海外 Dreamina 2.5 素材库”页管理，不与这里混用。</p>
          </div>
          <button type="button" onClick={refresh} disabled={busy} className="flex items-center gap-1 rounded-lg border border-[var(--color-border-primary)] px-3 py-1.5 text-xs disabled:opacity-50"><RefreshCw size={13} />刷新</button>
        </div>
        {loading ? <div className="mt-5 flex items-center gap-2 text-sm text-[var(--color-text-muted)]"><Loader2 size={16} className="animate-spin" />读取配置…</div> : info?.configured ? (
          <div className="mt-5 grid gap-3 rounded-xl border border-[var(--color-success)]/40 bg-[var(--color-bg-tertiary)] p-4 text-sm sm:grid-cols-2">
            <div className="flex items-center gap-2 font-medium text-[var(--color-success)]"><CheckCircle2 size={16} />已绑定新代理库</div>
            <div className="break-all font-mono text-xs">{info.groupId}</div>
            <div className="text-xs text-[var(--color-text-muted)]">ProjectName</div>
            <div className="break-all font-mono text-xs">{info.projectName}</div>
          </div>
        ) : <div className="mt-5 flex items-center gap-2 rounded-xl border border-[var(--color-border-primary)] p-3 text-sm text-[var(--color-text-muted)]"><AlertTriangle size={16} />尚未绑定；海外 2.0 新任务提交前会明确报错，不会扣费或误用旧库。</div>}
        {error && <div role="alert" className="mt-4 rounded-xl border border-[var(--color-error)] px-3 py-2 text-sm text-[var(--color-error)]">{error}</div>}
      </div>

      <div className="rounded-2xl border border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] p-5">
        <h2 className="text-sm font-semibold">查询并绑定官方素材组</h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">ProjectName 是官方资源空间，不是素材组名称。先查询，若已有组直接绑定；绑定会用当前视频 API Key 回读校验。</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
          <input className={inputClass} value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder="官方 ProjectName" aria-label="官方 ProjectName" />
          <button type="button" disabled={busy} onClick={searchGroups} className="rounded-xl border border-[var(--color-border-primary)] px-4 py-2 text-sm disabled:opacity-50">查询该空间的素材组</button>
        </div>
        {groups.length > 0 && <div className="mt-3 max-h-48 space-y-2 overflow-y-auto">
          {groups.map((item) => {
            const id = readText(item?.Id, item?.id, item?.GroupId, item?.groupId);
            return <div key={id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[var(--color-bg-tertiary)] p-2 text-xs">
              <span className="break-all font-mono">{readText(item?.Name, item?.name)} · {id}</span>
              <button type="button" disabled={busy || !id} onClick={() => bindGroup(id)} className="rounded-lg border border-[var(--color-border-primary)] px-2 py-1 disabled:opacity-50">绑定此组</button>
            </div>;
          })}
        </div>}
        <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
          <input className={inputClass} value={groupId} onChange={(event) => setGroupId(event.target.value)} placeholder="已有 group-… 素材组 ID" aria-label="已有素材组 ID" />
          <button type="button" disabled={busy} onClick={() => bindGroup()} className="rounded-xl bg-[var(--color-success)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">校验并绑定</button>
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] p-5">
        <h2 className="text-sm font-semibold">没有素材组？创建候选组</h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">创建不会自动切换当前绑定。请求结果不明确时先查询官方素材组，不要重复创建。</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input className={inputClass} value={groupName} onChange={(event) => setGroupName(event.target.value)} placeholder="素材组名称 Name" aria-label="素材组名称" />
          <input className={inputClass} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="描述（可选）" aria-label="素材组描述" />
        </div>
        {info?.configured && <label className="mt-3 flex items-start gap-2 text-xs text-[var(--color-text-muted)]"><input type="checkbox" checked={confirmAdditional} onChange={(event) => setConfirmAdditional(event.target.checked)} />我知晓已绑定素材组，额外创建可能产生重复；本次不会自动切换。</label>}
        <button type="button" disabled={busy} onClick={createGroup} className="mt-3 rounded-xl border border-[var(--color-border-primary)] px-4 py-2 text-sm disabled:opacity-50">{busy ? '处理中…' : '创建候选素材组'}</button>
        {candidateId && <p className="mt-3 break-all text-xs text-[var(--color-success)]">候选组 {candidateId} 已创建，尚未绑定。请在上方确认绑定。</p>}
      </div>

      {info?.configured && <div className="rounded-2xl border border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] p-5">
        <h2 className="text-sm font-semibold">当前新代理库素材 · {assets.length}</h2>
        <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
          {assets.length ? assets.map((item, index) => <div key={readText(item?.Id, item?.id) || index} className="flex flex-wrap justify-between gap-2 rounded-lg bg-[var(--color-bg-tertiary)] p-2 text-xs">
            <span className="break-all">{readText(item?.Name, item?.name, '未命名')} · {readText(item?.Id, item?.id)}</span>
            <span className="text-[var(--color-text-muted)]">{readText(item?.Status, item?.status, 'Unknown')}</span>
          </div>) : <p className="text-xs text-[var(--color-text-muted)]">暂无素材</p>}
        </div>
      </div>}
    </div>
  );
}
